// Composables
import {
  createRouter,
  createWebHistory,
  type RouteComponent,
} from "vue-router";
import { moduleRoutesBuilder } from "@/router/moduleRoutesBuilder";
import mitt from "@/application/plugins/mitt";
import i18n from "@/application/plugins/i18n";
import { AppIdentifier } from "@/application/enums";
import { SharedModule } from "@/modules/shared/enums";
import authGuard from "@/router/guards/authGuard";
import { AllModules } from "@/application/composables/module";
import { useApp } from "@/application/composables/app";

const { appData } = useApp();

const layouts: Record<AppIdentifier, () => Promise<RouteComponent>> = {
  [AppIdentifier.CentralAdmin]: () =>
    import("@/application/layouts/AuthorisedLayout.vue"),
  [AppIdentifier.CentralWebsite]: () =>
    import("@/application/layouts/CentralWebsiteLayout.vue"),
  [AppIdentifier.TenantAdmin]: () =>
    import("@/application/layouts/AuthorisedLayout.vue"),
  [AppIdentifier.TenantWebsite]: () =>
    import("@/application/layouts/PublicWebsiteLayout.vue"),
};

const unauthorizedLayouts: Record<
  AppIdentifier,
  () => Promise<RouteComponent>
> = {
  [AppIdentifier.CentralWebsite]: () =>
    import("@/application/layouts/CentralWebsiteLayout.vue"),
  [AppIdentifier.TenantWebsite]: () =>
    import("@/application/layouts/PublicWebsiteLayout.vue"),
  [AppIdentifier.CentralAdmin]: () =>
    import("@/application/layouts/UnauthorizedLayout.vue"),
  [AppIdentifier.TenantAdmin]: () =>
    import("@/application/layouts/UnauthorizedLayout.vue"),
};

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "",
      component: layouts[import.meta.env.VITE_APP_IDENTIFIER as AppIdentifier],
      children: moduleRoutesBuilder([SharedModule.Authorisation]).concat([
        {
          path: "",
          redirect: appData.landingPage,
        },
      ]),
    },
    {
      path: "",
      component:
        unauthorizedLayouts[
          import.meta.env.VITE_APP_IDENTIFIER as AppIdentifier
        ],
      children: moduleRoutesBuilder(
        AllModules.filter((module) => module !== SharedModule.Authorisation),
      ).concat([
        {
          path: "/error/:errorCode",
          name: "error",
          component: () => import("@/modules/shared/error/views/ErrorView.vue"),
          meta: {
            module: SharedModule.Error,
          },
        },
      ]),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "error.404",
      redirect: { name: "error", params: { errorCode: "404" } },
    },
  ],
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
});

router.beforeEach(authGuard);

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err) => {
  if (err?.message?.includes?.("Failed to fetch dynamically imported module")) {
    mitt.emit("snackbar", {
      text: i18n.global.t("errors.updateAvailable"),
      color: "error",
      location: "top center",
      persistent: true,
      actions: [
        {
          text: i18n.global.t("actions.refreshPage"),
          action: () => window.location.reload(),
          icon: "$sync",
        },
      ],
    });
  }
});

export default router;
